import { Box, Grid, Slider } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { AnalysisSettings, AnalysisType } from 'features/analysis/types'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import AnalysisHeader from '../AnalysisHeader'
import { BufferMeasure, BufferSettings, BufferType } from './types'
import { generateBufferQuery } from './queries'
import { setSettingsSnapshot } from 'features/analysis/state/analysisSlice'
import MeasureDropdown from './Form/MesaureDropdown'
import RadiusInput from './Form/RadiusInput'
import TractsDropdown from './Form/TractsDropdown'
import BufferTypeButtons from './Form/BufferTypeButtons'
import AnalysisBase from '../AnalysisBase'
import { AppFormInputLabel, AppFormGridColumn } from 'features/apps/ui/AppForm/appsFormStyle'
import { Connection, getConnectionByName } from 'features/connections'
import { useATLocation } from 'features/connections/hooks/useATLocation'
import { useSourceRegion } from 'features/builder/hooks/builderHooks'

function BufferForm({
  settings,
  onChange,
  onQueryChange
}: {
  settings: BufferSettings
  onChange: (analysis: AnalysisSettings) => void
  onQueryChange: (query: string | undefined) => void
}) {
  const dispatch = useAppDispatch()
  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId) || ''
  const sourceDataset = useAppSelector((state) => getEnrichedDatasetById(state, sourceDatasetId as string))
  const connection = useAppSelector((state) =>
    getConnectionByName(state, { connection: sourceDataset.connectionName })
  ) as Connection
  const [radius, setRadius] = useState(settings?.radius || 10)
  const [radiusMeasure, setRadiusMeasure] = useState<BufferMeasure>(settings?.measure || BufferMeasure.Kilometers)
  const [tracts, setTracts] = useState(settings?.tracts || 1)
  const [type, setType] = useState<BufferType>(settings?.type || BufferType.NonCombined)

  const { data: tableRegion } = useSourceRegion(sourceDataset, connection)
  const { atLocation } = useATLocation({ connectionId: connection?.id, region: tableRegion })

  useEffect(() => {
    if (sourceDataset && radius && radiusMeasure && type && tracts && atLocation) {
      onQueryChange(
        generateBufferQuery({
          mainSource: sourceDataset,
          radius,
          measure: radiusMeasure,
          type,
          tracts,
          atLocation
        })
      )
    }
  }, [onQueryChange, sourceDataset, radius, radiusMeasure, type, tracts, atLocation])

  useEffect(() => {
    return () => {
      const settings: BufferSettings = {
        mainSource: sourceDatasetId,
        radius,
        measure: radiusMeasure,
        type,
        tracts,
        analysisType: AnalysisType.Buffer
      }
      dispatch(setSettingsSnapshot(settings))
      onChange(settings)
    }
  }, [sourceDatasetId, radius, radiusMeasure, type, tracts, onChange, dispatch])

  const handleRadiusSlideChange = (event, newValue) => {
    setRadius(newValue)
  }

  const handleRadiusInputChange = (value) => {
    setRadius(value)
  }

  return (
    <>
      <AnalysisHeader analysisType={AnalysisType.Buffer} />
      <Grid container spacing={4} mt={3} mb={2}>
        <AppFormGridColumn item sm={6}>
          <AnalysisBase analysisType={AnalysisType.Buffer} />
        </AppFormGridColumn>

        <Grid item sm={6}>
          <Box mb={1.5}>
            <Typography variant="subtitle2">
              <FormattedMessage id={`analysis.buffer.params`} />
            </Typography>
          </Box>

          <Grid container direction="row" alignItems="center" pt={0.75}>
            <Grid item xs>
              <AppFormInputLabel id="distance-slide-label" variant="outlined">
                <FormattedMessage id={`analysis.buffer.distance`} />
              </AppFormInputLabel>
              <Slider
                name="distance-slide-label"
                min={1}
                max={100}
                value={typeof radius === 'number' ? radius : 0}
                onChange={handleRadiusSlideChange}
              />
            </Grid>

            <Grid item pl={3} pt={1.5}>
              <RadiusInput radius={radius} onChange={handleRadiusInputChange} />
            </Grid>

            <Grid item pl={1.5} pt={1.5}>
              <MeasureDropdown radiusMeasure={radiusMeasure} onChange={(value) => setRadiusMeasure(value)} />
            </Grid>

            <Grid container direction="row" alignItems="center" pt={2}>
              <Grid item>
                <TractsDropdown tracts={tracts} onChange={(value) => setTracts(value)} />
              </Grid>
              <Grid item xs pl={2} pt={3}>
                <BufferTypeButtons onChange={(value) => setType(value)} provider={sourceDataset.providerId} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default BufferForm
